import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'admin_token';

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` },
});

const useAdminData = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(sessionStorage.getItem(TOKEN_KEY)));
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [inquiriesRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/inquiries`, authHeaders()),
        axios.get(`${BACKEND_URL}/api/admin/stats`, authHeaders()),
      ]);
      setInquiries(inquiriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load dashboard data');
    }
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, response.data.access_token);
      setIsAuthenticated(true);
      toast.success('Login successful!');
      await refresh();
    } catch (error) {
      console.error('Admin login failed:', error);
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      toast.error(isAuthError ? 'Invalid credentials' : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const updateStatus = async (inquiryId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/inquiries/${inquiryId}`, { status }, authHeaders());
      toast.success('Status updated');
      await refresh();
    } catch (error) {
      console.error('Failed to update inquiry status:', error);
      toast.error('Failed to update status');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem(TOKEN_KEY)) {
      refresh();
    }
  }, [refresh]);

  return { isAuthenticated, inquiries, stats, loading, login, logout, updateStatus };
};

export default useAdminData;
